import React from 'react';
import { useTable, useBlockLayout } from 'react-table';
import { useSticky } from 'react-table-sticky';
import Inputs from 'components/inputs/inputs';
import Helpers from 'utils/helpers';
import './datatable.css';

export default function Datatable(props) {
  const columns = props.columns || [];
  const data = props.data || [];
  const hideHeader = props.hideHeader || false;
  const hideFooter = props.hideFooter || true;
  let hasGrowColumn = false;

  const defaultColumn = {
    minWidth: 30,
    width: 160,
    maxWidth: 400,
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: { hiddenColumns: columns.filter((c) => c.isVisible === false).map((c) => c.accessor) }
    },
    useBlockLayout,
    useSticky,
  );

  // Workaround as react-table footerGroups doesn't provide the same internal data as headerGroups
  const footerGroups = headerGroups.slice().reverse();

  const prepareTable = () => {
    headerGroups.map((hg) => hg.headers.map((h) => hasGrowColumn = hasGrowColumn || h.grow));
  };

  const handleRowClick = (row) => {
    if (props.onRowClick) {
      props.onRowClick(row?.values);
    }
  };

  const getRowClass = (type, index) => {
    let classNames = [type];
    if (Helpers.Object.exists(index)) {
      classNames.push((index % 2 === 0) ? 'evenRow' : 'oddRow');
    }
    if (props.onRowClick) {
      classNames.push('activeRow');
    }
    return Helpers.String.join(classNames);
  }

  const getColumnClass = (type, index, item) => {
    let classNames = [type];
    if (Helpers.Object.exists(index)) {
      classNames.push((index % 2 === 0) ? 'evenCol' : 'oddCol');
    }
    item = (item.column) ? item.column : item;
    if (item) {
      if (item.className) {
        classNames.push(item.className);
      }
      if (item.align) {
        classNames.push(`text-${item.align}`);
      }
      if (item.grow) {
        classNames.push('flex-grow-1');
      }
      if (item.group) {
        classNames.push('groupCol');
      }
    }
    return Helpers.String.join(classNames);
  };

  const getHeaderRowProps = (row) => {
    const rowProps = { ...row.getHeaderGroupProps() };
    if (hasGrowColumn && rowProps.style && rowProps.style.width) {
      rowProps.style.minWidth = rowProps.style.width;
      delete rowProps.style.width;
    }
    return rowProps;
  };

  const getHeaderColumnProps = (column) => {
    const columnProps = { ...column.getHeaderProps() };
    return columnProps;
  };

  const getRowProps = (row) => {
    const rowProps = { ...row.getRowProps() };
    if (hasGrowColumn && rowProps.style && rowProps.style.width) {
      rowProps.style.minWidth = rowProps.style.width;
      delete rowProps.style.width;
    }
    return rowProps;
  };

  const getColumnProps = (column) => {
    const columnProps = { ...column.getCellProps() };
    return columnProps;
  };

  if (rows.length === 0 && React.Children.count(props.children) > 0) {
    return props.children;
  }
  else {
    prepareTable();
    return (
      <div {...getTableProps()} className='table sticky' style={{ maxHeight: props.maxHeight || 500 }}>
        {hideHeader ? null :
          <div className='thead'>
            {headerGroups.map((row) => (
              <div {...getHeaderRowProps(row)} className='tr'>
                {row.headers.map((column, columnIndex) => (
                  <div {...getHeaderColumnProps(column)} className={getColumnClass('th', columnIndex, column)}>
                    {column.render('Header')}
                  </div>
                ))}
              </div>
            ))}
          </div>
        }
        <div {...getTableBodyProps()} className='tbody'>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <div {...getRowProps(row)} className={getRowClass('tr', rowIndex)} onClick={() => handleRowClick(row)}>
                {row.cells.map((cell, cellIndex) => (
                  <div {...getColumnProps(cell)} className={getColumnClass('td', rowIndex, cell)}>
                    <CellContents cell={cell} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        {hideFooter ? null :
          <div className='tfoot'>
            {footerGroups.map((row) => (
              <div {...getHeaderRowProps(row)} className='tr'>
                {row.headers.map((column, columnIndex) => (
                  <div {...getHeaderColumnProps(column)} className={getColumnClass('td', columnIndex, column)}>
                    {column.render('Footer')}
                  </div>
                ))}
              </div>
            ))}
          </div>
        }
      </div>
    );
  }
}

function CellContents(props) {
  const cell = props.cell;

  const renderIndicator = () => {
    if (cell.column.indicator) {
      const value = cell.row.values[cell.column.id];
      for (let i = 0; i < cell.column.indicator.length; i++) {
        const rule = cell.column.indicator[i];
        if (value === rule.equals) {
          return <span className='indicator' style={{ backgroundColor: rule.color, boxShadow: '0px 0px 4px 2px ' + rule.color }}></span>;
        }
      }
    }
    return null;
  };

  const renderProgress = () => {
    if (cell.column.progress) {
      const value = cell.row.values[cell.column.id];
      let color = null;
      for (let i = 0; i < cell.column.progress.length; i++) {
        const rule = cell.column.progress[i];
        if (value >= rule.start && value <= rule.end) {
          color = rule.color;
          break;
        }
      }
      return (
        <div className='progress progress-xs'>
          <div className='progress-bar' style={{ width: value + '%', backgroundColor: color }}></div>
        </div>
      );
    }
  };

  const renderProgressBlocks = () => {
    if (cell.column.progressBlocks) {
      const value = cell.row.values[cell.column.id];
      let color = null;
      for (let i = 0; i < cell.column.progressBlocks.length; i++) {
        const rule = cell.column.progressBlocks[i];
        if (value >= rule.value) {
          color = rule.color;
        }
      }
      return (
        <div className='progress-blocks progress-blocks-xs'>
          {cell.column.progressBlocks.map((rule, index) => {
            return <div className='progress-block' style={{ backgroundColor: (value >= rule.value) ? color : null }} key={index}></div>;
          })}
        </div>
      );
    }
  };

  const renderValue = () => {
    if (cell.column.format) {
      const value = cell.row.values[cell.column.id];
      return cell.column.format(value, cell);
    }
    return cell.render('Cell');
  };

  const renderContent = () => {
    if (cell.column.customFunction) {
      const value = cell.row.values[cell.column.id];
      return cell.column.customFunction(value, cell);
    }
    if (cell.column.badge) {
      const value = cell.row.values[cell.column.id];
      for (let i = 0; i < cell.column.badge.length; i++) {
        const rule = cell.column.badge[i];
        if (value === rule.equals) {
          return <span className='label' style={{ backgroundColor: rule.color, color: rule.textColor }}>{renderValue()}</span>;
        }
      }
    }
    if (cell.column.progress) {
      return null;
    }
    if (cell.column.progressBlocks) {
      return null;
    }
    if (cell.column.checkbox) {
      return (
        <Inputs.Checkbox variant={cell.column.checkbox.variant} onValueChange={(v) => (cell.column.checkbox.onChange ? cell.column.checkbox.onChange(v, cell) : null)}>
          {renderValue()}
        </Inputs.Checkbox>
      );
    }
    return <span>{renderValue()}</span>;
  };

  return (
    <>
      {renderIndicator()}
      {renderProgress()}
      {renderProgressBlocks()}
      {renderContent()}
    </>
  );
}